import { $actionEvent } from '../../actions/actionEvent';
import { $element } from '../RichTextEditor';
import './ActionButton.css';
import { RibbonTooltip } from './RibbonTooltip';
import { toCamelCase } from '@/utils/utils';
import { Icon } from '@iconify/react';
import clsx from 'clsx';
import { DropdownMenu, Tooltip } from 'radix-ui';
import React, { useEffect, useState } from 'react';

export function ActionButton({
  active = false,
  disabled = false,
  name,
  tooltip = name,
  icon,
  actionName = toCamelCase(name),
  className,
  hideText = false,
  // @ts-expect-error can't be arsed to properly type
  onClick = e => $actionEvent.emit(actionName),
}: {
  active?: boolean;
  disabled?: boolean;
  name: string;
  tooltip?: string;
  icon: React.ReactNode;
  actionName?: string;
  className?: string;
  hideText?: boolean;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
}) {
  return (
    <RibbonTooltip name={tooltip}>
      <button
        type="button"
        tabIndex={-1}
        className={clsx(
          'group relative flex cursor-pointer items-center gap-2 rounded p-2 transition disabled:cursor-not-allowed disabled:opacity-50',
          active ? 'bg-gray-200' : 'not-disabled:hover:bg-gray-100',
          className,
        )}
        onClick={onClick}
        disabled={disabled}
      >
        {icon}
        {!hideText && (
          <span className="show-in-menu text-sm leading-none">{name}</span>
        )}
      </button>
    </RibbonTooltip>
  );
}

export function TextActionButton({
  activeCb,
  ...props
}: React.ComponentProps<typeof ActionButton> & {
  activeCb: () => boolean;
}) {
  const [active, setActive] = useState(() => activeCb());

  useEffect(() => {
    function onSelectionChange() {
      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0) {
        setActive(false);
        return;
      }
      const range = sel.getRangeAt(0);
      const el = $element.get();
      if (!el || !el.contains(range.commonAncestorContainer)) {
        setActive(false);
        return;
      }
      setActive(activeCb());
    }

    document.addEventListener('selectionchange', onSelectionChange);
    return () =>
      document.removeEventListener('selectionchange', onSelectionChange);
  }, [activeCb]);

  return <ActionButton {...props} active={active} />;
}

export function ActionButtonMenu({
  children,
  dropDownDisabled = false,
  ...props
}: React.ComponentProps<typeof ActionButton> & {
  children: React.ReactNode;
  dropDownDisabled?: boolean;
}) {
  return (
    <div className="flex">
      <ActionButton {...props} className="rounded-r-none" />
      <DropdownMenu.Root>
        <DropdownMenu.Trigger disabled={dropDownDisabled}>
          <div
            className={clsx(
              'flex h-full items-center rounded rounded-l-none',
              dropDownDisabled
                ? 'cursor-not-allowed opacity-50'
                : 'cursor-pointer hover:bg-gray-100',
            )}
          >
            <Icon icon="fluent:chevron-down-20-regular" />
          </div>
        </DropdownMenu.Trigger>
        <DropdownMenu.Portal>
          <DropdownMenu.Content
            className="in-menu mt-1 rounded border border-black/10 bg-white shadow"
            onMouseDown={e => e.stopPropagation()}
            onKeyDown={e => e.stopPropagation()}
            onClick={e => e.stopPropagation()}
            onFocus={e => e.stopPropagation()}
          >
            {children}
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
    </div>
  );
}

export function ActionMenu({
  disabled = false,
  name,
  icon,
  children,
}: {
  disabled?: boolean;
  name: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="flex">
      <DropdownMenu.Root>
        <DropdownMenu.Trigger disabled={disabled}>
          <RibbonTooltip name={name}>
            <div
              className={clsx(
                'flex h-full items-center rounded p-2 pr-0',
                disabled
                  ? 'cursor-not-allowed opacity-50'
                  : 'cursor-pointer hover:bg-gray-100',
              )}
            >
              {icon}
              <Icon icon="fluent:chevron-down-20-regular" />
            </div>
          </RibbonTooltip>
        </DropdownMenu.Trigger>
        <DropdownMenu.Portal>
          <DropdownMenu.Content
            className="in-menu mt-1 rounded border border-black/10 bg-white shadow"
            onMouseDown={e => e.stopPropagation()}
            onKeyDown={e => e.stopPropagation()}
            onClick={e => e.stopPropagation()}
          >
            {children}
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
    </div>
  );
}
